/**
 * Project Feature Row
 *
 * One feature in a project's assignment list: toggle, name, version (for the
 * assigned line), category, gate, and option / runtime-config controls.
 */

import type { ReactElement } from 'react';
import { OptionFieldRenderer } from '../OptionFieldRenderer';
import { RuntimeConfigButton } from './RuntimeConfigButton';
import { MissingRuntimeConfigBadge } from './MissingRuntimeConfigBanner';
import { FeatureVersion } from './FeatureVersion';
import { lineOptionSchema } from './lineOptionSchema';
import { autoClearDependents } from '../../utils/autoClearDependents';
import type { Feature, MissingRuntimeConfigItem } from '../../schemas/admin.schemas';

interface ProjectFeatureRowProps {
  feature: Feature;
  projectId: string;
  projectName: string;
  isAssigned: boolean;
  isPackLinked: boolean;
  isExpanded: boolean;
  unconfigured: boolean;
  requiredBy?: string[] | undefined;
  missingItem?: MissingRuntimeConfigItem | undefined;
  /** Effective options: defaults plus the assignment's overrides (or the edit state when expanded). */
  optionValues: Record<string, unknown>;
  autoClearNote: string | null;
  onToggle: () => void;
  onExpand: () => void;
  onOptionsChange: (values: Record<string, unknown>, note: string | null) => void;
  onSaveOptions: () => void;
}

export function ProjectFeatureRow({
  feature, projectId, projectName, isAssigned, isPackLinked, isExpanded, unconfigured,
  requiredBy, missingItem, optionValues, autoClearNote,
  onToggle, onExpand, onOptionsChange, onSaveOptions,
}: ProjectFeatureRowProps): ReactElement {
  const hasOptions = Object.keys(feature.option_schema).length > 0;
  const lineValue = feature.variant_key ? optionValues[feature.variant_key] : undefined;
  return (
    <li className="project-features__item">
      <div className="project-features__row">
        <label className="project-features__cell-assigned">
          <input type="checkbox" checked={isAssigned} onChange={onToggle} disabled={isPackLinked} />
        </label>
        <span className="project-features__cell-feature">
          {feature.display_name}
          {requiredBy && (
            <span className="feature-table__badge" style={{ marginLeft: '0.5rem' }}
              title={feature.dependencies_note ?? `Required by: ${requiredBy.join(', ')}`}>
              dep: {requiredBy.join(', ')}
            </span>
          )}
          {isAssigned && <MissingRuntimeConfigBadge item={missingItem} />}
          {unconfigured && (
            <span className="project-features__warning" title="Has unconfigured required options">
              &#x26A0;
            </span>
          )}
        </span>
        <span className="project-features__cell-version feature-table__version">
          <FeatureVersion feature={feature} selectedLine={lineValue} />
        </span>
        <span className="project-features__cell-category">{feature.category}</span>
        <span className="project-features__cell-gate">
          {feature.is_gated && (
            <span className="feature-table__badge"
              title={`${feature.gate_attribute ?? ''} = ${feature.gate_value ?? ''}`}>
              {feature.gate_value ?? 'Gated'}
            </span>
          )}
        </span>
        <span className="project-features__cell-options">
          {isAssigned && hasOptions && !isPackLinked && (
            <button type="button" className="project-features__options-btn" onClick={onExpand}
              aria-label={`${isExpanded ? 'Collapse' : 'Expand'} options for ${feature.display_name}`}>
              {isExpanded ? 'Close' : 'Options'}
            </button>
          )}
          {isAssigned && (
            <RuntimeConfigButton
              featureId={feature.id}
              featureName={feature.display_name}
              projectId={projectId}
              projectName={projectName}
              className="project-features__options-btn"
              needsAttention={missingItem !== undefined}
            />
          )}
        </span>
      </div>
      {isAssigned && isExpanded && (
        <div className="project-features__options">
          {unconfigured && (
            <span className="project-features__warning-text">
              Configure required options below to enable this feature in workspaces.
            </span>
          )}
          {autoClearNote && (
            <div className="option-field__auto-clear-note" role="status">
              {autoClearNote}
            </div>
          )}
          <OptionFieldRenderer schema={lineOptionSchema(feature, lineValue)} values={optionValues}
            onChange={(key, value) => {
              const { values: next, note } = autoClearDependents(
                key, value, optionValues, feature.option_schema,
              );
              onOptionsChange(next, note);
            }}
            showAdminBadge={true} />
          <button type="button" className="dialog__btn dialog__btn--primary" onClick={onSaveOptions}>
            Save Options
          </button>
        </div>
      )}
    </li>
  );
}
